import { supabase } from '../auth/supabase-client';
import type { Liste, ListColor, ListKind } from './types';

function client() {
  if (!supabase) throw new Error('Supabase indisponible.');
  return supabase;
}

/**
 * Crée à la première ouverture ce que chacun s'attend à trouver : sa liste,
 * celles de ses pôles, celle de son agence, et « Direction » pour la direction.
 * Le travail est fait côté base parce qu'un membre simple n'a pas le droit de
 * créer la liste de son pôle.
 */
export async function preparerListesParDefaut(workspaceId: string): Promise<void> {
  const { error } = await client().rpc('ensure_default_lists', { p_workspace: workspaceId });
  if (error) throw error;
}

export async function chargerListes(workspaceId: string): Promise<Liste[]> {
  const { data, error } = await client()
    .from('lists')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('position')
    .order('created_at');
  if (error) throw error;
  return data ?? [];
}

export type NouvelleListe = {
  name: string;
  kind: ListKind;
  owner_member_id?: string | null;
  managed_by_member_id?: string | null;
  pole_id?: string | null;
  site_id?: string | null;
  color?: ListColor | null;
};

export async function creerListe(
  workspaceId: string,
  valeurs: NouvelleListe,
  position: number,
): Promise<Liste> {
  const { data, error } = await client()
    .from('lists')
    .insert({
      workspace_id: workspaceId,
      name: valeurs.name,
      kind: valeurs.kind,
      owner_member_id: valeurs.owner_member_id ?? null,
      managed_by_member_id: valeurs.managed_by_member_id ?? null,
      pole_id: valeurs.pole_id ?? null,
      site_id: valeurs.site_id ?? null,
      color: valeurs.color ?? null,
      position,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Ce qui se change sur une liste après sa création, l'audience comprise : une
 * liste montée « pour moi » devient celle d'un pôle sans qu'il faille la
 * refaire et redéplacer ses tâches.
 */
export type ChampsListe = {
  name?: string;
  color?: ListColor | null;
  archived?: boolean;
  kind?: ListKind;
  owner_member_id?: string | null;
  managed_by_member_id?: string | null;
  pole_id?: string | null;
  site_id?: string | null;
};

export async function majListe(listeId: string, valeurs: ChampsListe): Promise<void> {
  const { error } = await client().from('lists').update(valeurs).eq('id', listeId);
  if (error) throw error;
}

export async function supprimerListe(listeId: string): Promise<void> {
  const { error } = await client().from('lists').delete().eq('id', listeId);
  if (error) throw error;
}

export async function reordonnerListes(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await client().rpc('reorder_lists', { p_ids: ids });
  if (error) throw error;
}
